import { Injectable } from '@angular/core'
import {
  conditionFields,
  evaluateLoadoutCondition,
} from '../edit-transaction/loadout-condition'

import { RpsSource } from '../models/field-catalog.model'
import {
  DEFAULT_SOURCE,
  GridRole,
  RPS_SOURCE,
} from '../models/source-key.model'
import {
  CreateMenuItem,
  KeyGroup,
  ListRecord,
  listRequestKeys,
  listValueOptions,
  TranCategoryDefinition,
  TranCategoryKey,
  TransactionFieldMetadata,
  TransactionMetadataConfig,
} from '../models/transaction-metadata.model'
import TRANSACTION_METADATA_CONFIG from '../registry/catalogs/transaction-metadata.config.json'
import { WorkbenchRow } from '../models/workbench-row.model'
import { adaptTransactionMetadata } from './transaction-metadata.adapter'

/** The grid role the dialog edits. */
export const METADATA_GRID_ROLE: GridRole = 'base'

export const METADATA_SOURCE: RpsSource =
  RPS_SOURCE[DEFAULT_SOURCE[METADATA_GRID_ROLE]]

/** The shared trade core. Named once here because two derivations have to exclude it. */
const COMMON_KEY_GROUP: KeyGroup = 'common'

/**
 * Seam between the Create/Edit Transaction modal and the field-metadata layer, mirroring the
 * {@link FieldCatalogService} / {@link WorkbenchDataSource} seams.
 *
 * Phase 1 binds {@link StaticTransactionMetadataService}, which serves the checked-in config. When
 * the metadata service exists, a second implementation is provided in its place and no consumer
 * changes: consumers only ever ask for "the fields of this tran category, in order".
 */
export abstract class TransactionMetadataSource {
  /** The document as it was served. Every read inside this class goes through {@link resolved}. */
  abstract config(): TransactionMetadataConfig

  readonly source: RpsSource = METADATA_SOURCE

  /** Derivations cached against the DOCUMENT ITSELF, not a boolean. */
  private derivedFrom?: TransactionMetadataConfig
  private categoryByKeyGroup = new Map<KeyGroup, TranCategoryKey>()
  private keyGroups: readonly KeyGroup[] = []
  private routedFields: readonly string[] = []

  /** Clashing condition sets already reported, so a grid resolving thousands of rows says it once. */
  private readonly ambiguityWarned = new Set<string>()

  // ── Resolved catalog lists (PVAL-26443) ──────────────────────────────────────────────────
  // A `catalog` list is served WITHOUT its options: the document carries `status: 'unavailable'`
  // and no upstream, because RPS's served model has no property a URL could occupy. The options
  // come separately, from `POST dialog-metadata/resolve`, and are merged back onto the document
  // here rather than carried beside it — `listOptionsOf` then finds them exactly where it finds a
  // `static` list's, and every consumer stays unaware the two arrived by different routes.

  /**
   * Keyed by field name: the ROWS `/resolve` answered (PVAL-26685), read later through the field's
   * own display keys. Survives the document being replaced, since the rows outlive it.
   */
  private readonly resolvedLists = new Map<string, ListRecord[]>()

  /** The document the current overlay was built from, so it is rebuilt only when either moves. */
  private overlaidFrom?: TransactionMetadataConfig
  private overlaid?: TransactionMetadataConfig

  /**
   * The served document with every resolved catalog list merged in. **Read this, not `config()`.**
   *
   * `config()` stays the implementations' own method rather than becoming a template over an
   * abstract `document()`: the subclasses in the specs name a `document` of their own, and this is
   * not worth a rename across four of them. Everything in this class that needs fields goes through
   * here instead, and nothing outside it calls `config()` today.
   *
   * Returns the document ITSELF while nothing is resolved, so the identity `derive()` caches
   * against is stable on the common path and a panel that never opens a dialog pays nothing.
   */
  protected resolved(): TransactionMetadataConfig {
    const served = this.config()
    if (this.resolvedLists.size === 0) return served
    if (this.overlaidFrom === served && this.overlaid) return this.overlaid

    const overlaid: TransactionMetadataConfig = {
      ...served,
      fields: served.fields.map(field => {
        const rows = this.resolvedLists.get(field.field)
        // Only a `catalog` list is served without its rows, so only one is ever filled in.
        if (!rows || field.listValues?.mode !== 'catalog') return field
        return { ...field, listValues: { ...field.listValues, options: rows } }
      }),
    }
    this.overlaidFrom = served
    this.overlaid = overlaid
    return overlaid
  }

  /**
   * The field names whose list is declared and still has no options — what `/resolve` is asked for.
   *
   * `search` is excluded by `minChars`: a typeahead is resolved per keystroke against the user's
   * own text, so asking for it on open would be asking for an unbounded set with no query.
   */
  unresolvedListKeys(): readonly string[] {
    return this.resolved()
      .fields.filter(
        field =>
          field.listValues != null &&
          field.listValues.mode !== 'search' &&
          listValueOptions(field.listValues) === null,
      )
      .map(field => field.field)
  }

  /**
   * The `params` keys a field's `/resolve` call takes, as the document serves them (PVAL-26685).
   * Empty when the field has no list, or a list that takes none.
   *
   * RPS defines the names, not the UI: the stored url and body carry them, and the document serves
   * only the names. The caller sends the ones it holds a value for.
   */
  requestKeysOf(field: string): readonly string[] {
    return listRequestKeys(
      this.resolved().fields.find(entry => entry.field === field)?.listValues,
    )
  }

  /**
   * The property of a derivation's reply that holds the value, as the document serves it
   * (PVAL-26685). Undefined for a field that is not derived, or one RPS names no property for.
   */
  derivationDisplayTextOf(field: string): string | undefined {
    return this.resolved().fields.find(entry => entry.field === field)
      ?.derivationDisplayText
  }

  /**
   * Merge resolved rows in. Empty input is a no-op, so a failed resolve changes nothing. A catalog
   * with at least one row is resolved from then on.
   */
  applyResolvedLists(options: ReadonlyMap<string, ListRecord[]>): void {
    if (options.size === 0) return
    options.forEach((rows, field) => {
      if (rows.length) this.resolvedLists.set(field, rows)
    })
    // Both caches are now stale: the overlay by construction, and `derive()`'s because the
    // identity it keys on is about to change.
    this.overlaidFrom = undefined
    this.overlaid = undefined
  }

  private derive(): void {
    const config = this.resolved()
    if (this.derivedFrom === config) return

    const byGroup = new Map<KeyGroup, TranCategoryKey>()
    const groups = new Set<KeyGroup>()
    const routed = new Set<string>()
    for (const category of config.tranCategories) {
      for (const group of category.keyGroups) {
        groups.add(group)
        if (group !== COMMON_KEY_GROUP) byGroup.set(group, category.key)
      }
      for (const field of conditionFields(category.loadoutCondition)) {
        routed.add(field)
      }
    }

    this.categoryByKeyGroup = byGroup
    this.keyGroups = [...groups]
    this.routedFields = [...routed]
    this.derivedFrom = config
  }

  /** The curated tran category definition, or undefined if the key is not curated. */
  tranCategory(key: TranCategoryKey): TranCategoryDefinition | undefined {
    return this.resolved().tranCategories.find(entry => entry.key === key)
  }

  /** The tran category a row opens — the first whose `loadoutCondition` the row satisfies. */
  tranCategoryFor(row: WorkbenchRow): TranCategoryKey | undefined {
    const matches = this.resolved().tranCategories.filter(category =>
      evaluateLoadoutCondition(category.loadoutCondition, row),
    )

    if (matches.length > 1) this.warnAmbiguous(matches)
    return matches[0]?.key
  }

  /** One line per clashing pair, not one per row — a grid resolves thousands. */
  private warnAmbiguous(matches: TranCategoryDefinition[]): void {
    const keys = matches.map(category => category.key).join(', ')
    if (this.ambiguityWarned.has(keys)) return
    this.ambiguityWarned.add(keys)
    console.error(
      `[transactions-2] loadout conditions are not mutually exclusive: ${keys} all match the same ` +
        `row. Opening ${matches[0].key}. Each condition must exclude the others — a trade category ` +
        `carries its own \`isNull gLEntryId\` rather than relying on evaluation order.`,
    )
  }

  /**
   * Every row field the document's loadout conditions route on, in first-seen order.
   *
   * ENFUSION names `instrumentType` and `gLEntryId`; another source names whatever its own
   * conditions do. Nothing in the panel may assert a routing field of its own — that is what made
   * the old `DISCRIMINATOR_FIELD` wrong for any source but the first.
   */
  conditionFields(): readonly string[] {
    this.derive()
    return this.routedFields
  }

  /** The tran category a key group opens — the inversion of `tranCategories[].keyGroups`. */
  tranCategoryForKeyGroup(group: KeyGroup): TranCategoryKey | undefined {
    this.derive()
    return this.categoryByKeyGroup.get(group)
  }

  /** Every key group an active category names, `common` included. */
  curatedKeyGroups(): readonly KeyGroup[] {
    this.derive()
    return this.keyGroups
  }

  /**
   * The Create menu, derived from `tranCategories` and nothing else.
   *
   * Each tran category contributes one leaf — `name` as the label, `key` as what it opens. A
   * `menuGroup` puts that leaf in a submenu whose label IS the `menuGroup` value; no `menuGroup`
   * puts it at the top level. There is no list of trade types here, no "Trade" literal, and no
   * key-to-label table: add a schema to the metadata and it appears in the menu, rename one and the
   * menu renames.
   *
   * **Document order is the menu order**, and a group takes the position of its FIRST member rather
   * than being appended. That is what keeps the fourteen trade types under one "Trade" flyout ahead
   * of the ungrouped `glEntry`, without the order being restated anywhere: it falls out of the
   * `category_order` the backend already sorts by.
   */
  createMenu(): readonly CreateMenuItem[] {
    const menu: CreateMenuItem[] = []
    const groups = new Map<
      string,
      CreateMenuItem & { children: CreateMenuItem[] }
    >()

    for (const tranCategory of this.resolved().tranCategories) {
      const leaf: CreateMenuItem = {
        label: tranCategory.name,
        tranCategory: tranCategory.key,
      }

      if (!tranCategory.menuGroup) {
        menu.push(leaf)
        continue
      }

      let group = groups.get(tranCategory.menuGroup)
      if (!group) {
        group = { label: tranCategory.menuGroup, children: [] }
        groups.set(tranCategory.menuGroup, group)
        menu.push(group)
      }
      group.children.push(leaf)
    }

    return menu
  }

  /**
   * Every shown field composing a tran category, in `order` order. A field belongs to the tran category when any
   * of its key groups is one the tran category composes, which is how the `common` core is shared between
   * the Listed trade and GL Entry contexts without being restated in either.
   */
  fieldsFor(key: TranCategoryKey): TransactionFieldMetadata[] {
    const definition = this.tranCategory(key)
    if (!definition) return []
    const groups = new Set(definition.keyGroups)
    return this.resolved()
      .fields.filter(
        field =>
          field.settings.shown &&
          field.keyGroups.some(group => groups.has(group)),
      )
      .sort((left, right) => left.order - right.order)
  }
}

/**
 * Phase 1: the checked-in metadata config, read from JSON rather than a TypeScript const so the
 * document that ships is the same document a CRUD metadata endpoint would later return — swapping
 * this provider for an HTTP one is then a transport change, not a re-authoring.
 *
 * {@link adaptTransactionMetadata} is the one place the document's widened primitive types (every
 * string is `string`) and its own spelling meet the model's. It is checked by
 * `transaction-metadata.config.spec.ts`, which validates the adapted document against those unions
 * on every run, so a bad edit fails a test rather than reaching a user.
 */
@Injectable()
export class StaticTransactionMetadataService extends TransactionMetadataSource {
  /**
   * Adapted once, not per call: the checked-in document never changes, and `config()` is read on
   * every projection pass and every `fieldsFor`.
   */
  private readonly adapted = adaptTransactionMetadata(
    TRANSACTION_METADATA_CONFIG,
  )

  config(): TransactionMetadataConfig {
    return this.adapted
  }
}
